<script setup lang="ts">
import {
  computed,
  ref,
  toRefs,
  provide,
  markRaw,
  effectScope,
  onUnmounted,
} from 'vue';
import { storeToRefs } from 'pinia';
import vtkInteractorStyleManipulator from '@kitware/vtk.js/Interaction/Style/InteractorStyleManipulator';
import { useVtkView } from '@/src/core/vtk/useVtkView';
import { useImage } from '@/src/composables/useCurrentImage';
import { useVtkInteractorStyle } from '@/src/core/vtk/useVtkInteractorStyle';
import { LPSAxisDir } from '@/src/types/lps';
import { useResizeObserver, watchImmediate } from '@vueuse/core';
import { resetCameraToImage, resizeToFitImage } from '@/src/utils/camera';
import { usePersistCameraConfig } from '@/src/composables/usePersistCameraConfig';
import { useAutoFitState } from '@/src/composables/useAutoFitState';
import { Maybe } from '@/src/types';
import { VtkViewApi } from '@/src/types/vtk-types';
import { VtkViewContext } from '@/src/components/vtk/context';
import { useViewCameraStore } from '@/src/store/view-configs/camera';

type Props = {
  viewId: string;
  imageId: Maybe<string>;
  viewDirection: LPSAxisDir;
  viewUp: LPSAxisDir;
};

const props = defineProps<Props>();
const {
  viewId: viewID,
  imageId: imageID,
  viewDirection,
  viewUp,
} = toRefs(props);

const vtkContainerRef = ref<HTMLElement>();
const viewCameraStore = useViewCameraStore();
const { disableCameraAutoReset } = storeToRefs(viewCameraStore);

const { metadata: imageMetadata } = useImage(imageID);

// use a detached scope so that actors can be removed from
// the renderer before the renderer is deleted.
const scope = effectScope(true);
const view = scope.run(() =>
  useVtkView(vtkContainerRef, {
    separateImageLayer: true,
    clippingBounds: computed(() => imageMetadata.value.worldBounds),
  })
)!;

onUnmounted(() => {
  scope.stop();
});

view.renderer.setBackground(0, 0, 0);
view.renderer.getActiveCamera().setParallelProjection(true);

// setup interactor style
const { interactorStyle } = useVtkInteractorStyle(
  vtkInteractorStyleManipulator,
  view
);
// Camera manipulators fit clipping to image bounds; widgets pick the upper layer.
interactorStyle.setFocusedRenderer(view.imageRenderer);

// bind slice and window configs
// resizeToFit camera controls
const autoFitEnabled = computed({
  get: () =>
    viewCameraStore.getConfig(viewID.value, imageID.value)?.autoFit ?? true,
  set: (autoFit) => {
    if (imageID.value) {
      viewCameraStore.updateConfig(viewID.value, imageID.value, { autoFit });
    }
  },
});

const autoFit = useAutoFitState(
  view.renderer.getActiveCamera(),
  autoFitEnabled
);

function autoFitImage() {
  if (!autoFit.autoFit.value) return;
  autoFit.withPaused(() => {
    resizeToFitImage(
      view,
      imageMetadata.value,
      viewDirection.value,
      viewUp.value
    );
  });
}

useResizeObserver(vtkContainerRef, () => {
  if (disableCameraAutoReset.value) return;
  autoFitImage();
});

function resetCamera() {
  autoFit.autoFit.value = true;
  autoFit.withPaused(() => {
    resetCameraToImage(
      view,
      imageMetadata.value,
      viewDirection.value,
      viewUp.value
    );
    autoFitImage();
  });
}

watchImmediate(
  [viewID, imageID, disableCameraAutoReset],
  ([viewID_, imageID_, noAutoReset]) => {
    if (
      imageID_ &&
      !viewCameraStore.isCameraInitialized(viewID_, imageID_) &&
      !noAutoReset
    ) {
      resetCamera();
      viewCameraStore.markCameraAsInitialized(viewID_, imageID_);
    }
  }
);

// Must run before the clipping-range watcher below: resetCameraClippingRange
// computes near/far from the camera's current position, so on remount the
// saved position must be syncRef'd back first or the slice falls outside
// the clip volume and the canvas goes black.
usePersistCameraConfig(viewID, imageID, view.renderer.getActiveCamera());

watchImmediate([imageMetadata, disableCameraAutoReset], () => {
  if (
    viewCameraStore.isCameraInitialized(viewID.value, imageID.value) ||
    disableCameraAutoReset.value
  ) {
    view.renderer.resetCameraClippingRange(imageMetadata.value.worldBounds);
  }
});

// exposed API
const api: VtkViewApi = markRaw({
  ...view,
  interactorStyle,
  resetCamera,
});

defineExpose(api);
provide(VtkViewContext, api);

function onPointerDown() {
  autoFit.resume();
}

function onPointerUp() {
  autoFit.pause();
}
</script>

<template>
  <div>
    <div
      ref="vtkContainerRef"
      class="view"
      @pointerdown.capture="onPointerDown"
      @pointerup.capture="onPointerUp"
    />
    <slot></slot>
  </div>
</template>

<style scoped>
.view {
  width: 100%;
  height: 100%;
}
</style>
